import { CheckCircleFilled, CheckOutlined, CopyOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Flex, Modal, Typography } from "antd";
import type { StandbyClipboardEvent } from "../../features/ticket/ticketSlice";
import {
  formatStandbyEndClipboard,
  formatStandbyReportedClipboard,
  type StandbyClipboardContext,
} from "../utils/formatStandbyClipboard";
import { useCopySession } from "../utils/useCopySession";
import styles from "./StandbyClipboardModal.module.css";

const COPY_ID = "standby-clipboard-message";

type StandbyClipboardModalProps = {
  event: StandbyClipboardEvent | null;
  context: StandbyClipboardContext;
  intl: IntlShape;
  onClose: () => void;
};

export const StandbyClipboardModal = ({
  event,
  context,
  intl,
  onClose,
}: StandbyClipboardModalProps) => {
  const { copiedId, copySession } = useCopySession();

  if (!event) return null;

  const isStart = event.kind === "start";
  const messageText = isStart
    ? formatStandbyReportedClipboard(context, {
        reason: event.reason,
        startedAt: event.occurredAt,
        plannedEnd: event.plannedEnd,
      })
    : formatStandbyEndClipboard(context, { endedAt: event.occurredAt });
  const isCopied = copiedId === COPY_ID;

  return (
    <Modal
      open
      centered
      onCancel={onClose}
      footer={
        <Flex gap="small" justify="flex-end">
          <Button onClick={onClose}>{intl.formatMessage({ id: "ticket_done" })}</Button>
          <Button
            type="primary"
            icon={isCopied ? <CheckOutlined /> : <CopyOutlined />}
            onClick={() => copySession(COPY_ID, messageText)}
          >
            {intl.formatMessage({
              id: isCopied ? "ticket_report_copied" : "standby_clipboard_copy",
            })}
          </Button>
        </Flex>
      }
    >
      <Flex vertical align="center" gap="middle">
        <CheckCircleFilled className={styles.successIcon} />
        <Typography.Title level={4} className={styles.heading}>
          {intl.formatMessage({
            id: isStart
              ? "standby_clipboard_started_heading"
              : "standby_clipboard_ended_heading",
          })}
        </Typography.Title>
        <Typography.Text type="secondary">
          {intl.formatMessage({ id: "standby_clipboard_hint" })}
        </Typography.Text>
        <Typography.Paragraph className={styles.messagePreview}>
          {messageText}
        </Typography.Paragraph>
      </Flex>
    </Modal>
  );
};
